import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Sparkles, AlertTriangle, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { publicTrack } from "@/lib/analytics";
import { useAuth } from "@/lib/auth-context";
import BrandLogo from "@/components/BrandLogo";

export default function TrySample() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [samples, setSamples] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");

  const firedView = React.useRef(false);
  useEffect(() => {
    if (!firedView.current) {
      firedView.current = true;
      publicTrack("guest_try_view");
    }
    api.get("/samples").then(({ data }) => setSamples(data.samples || [])).catch(() => {});
  }, []);

  const start = async (id) => {
    setErr("");
    setBusyId(id);
    publicTrack("guest_sample_card_click", { sample_id: id });
    try {
      if (!user) {
        const { data } = await api.post("/auth/guest");
        setUser(data);
      }
      navigate(`/app/live?sample=${id}`);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        setErr("You've reached today's guest limit. Create a free account to keep going, it takes 30 seconds.");
      } else {
        setErr(formatApiError(e));
      }
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-zinc-50 grain-overlay relative">
      <div className="ambient-bg absolute inset-0 pointer-events-none" />
      <header className="relative flex items-center justify-between max-w-6xl mx-auto px-6 py-5">
        <BrandLogo variant="lockup" iconSize={28} to="/" testid="try-brand" />
        <div className="text-sm text-zinc-400">
          Already have an account?{" "}
          <Link to="/login" data-testid="try-login-link" className="text-amber-400 hover:text-amber-300 font-medium">Sign in</Link>
        </div>
      </header>

      <main className="relative max-w-6xl mx-auto px-6 pt-10 pb-24">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-zinc-800 bg-zinc-900/60 px-3 py-1.5 mb-6">
            <Sparkles className="h-3.5 w-3.5 text-amber-400" />
            <span className="text-[11px] text-zinc-300">Free sample, no signup, no resume, no job description needed</span>
          </div>
          <h1 data-testid="try-heading" className="text-4xl sm:text-5xl tracking-tighter font-semibold leading-[1.08]">
            Experience a real AI interview
            <span className="block text-amber-400 mt-1">in the next two minutes.</span>
          </h1>
          <p className="mt-5 text-base text-zinc-400 leading-relaxed max-w-xl">
            Pick a sample candidate below. We've prepared their resume and the job description for you, so
            you'll face the same AI interviewer, live voice, and readiness feedback that our members use.
          </p>
        </div>

        {err && (
          <div data-testid="try-error" className="mt-8 max-w-xl text-sm text-rose-300 bg-rose-500/10 border border-rose-500/25 rounded-xl px-4 py-3 flex items-start gap-2.5">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              {err}
              <div className="mt-2">
                <Link to="/register" data-testid="try-error-signup" className="inline-flex items-center gap-1.5 text-amber-400 hover:text-amber-300 font-semibold">
                  Create your free account <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </div>
        )}

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {samples.length === 0 && !err && (
            <div className="text-sm text-zinc-500 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading sample candidates…
            </div>
          )}
          {samples.map((s) => (
            <button
              key={s.id}
              data-testid={`try-sample-${s.id}`}
              onClick={() => start(s.id)}
              disabled={!!busyId}
              className="group text-left rounded-2xl border border-zinc-800 bg-zinc-900/40 hover:border-amber-500/40 hover:bg-zinc-900/70 transition-colors p-5 disabled:opacity-60"
            >
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">{s.candidate}</div>
              <div className="text-base font-semibold text-zinc-50">{s.role}</div>
              <div className="text-xs text-zinc-500 mt-0.5">{s.company}</div>
              <div className="text-xs text-zinc-400 mt-3 leading-relaxed line-clamp-3">{s.background}</div>
              <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-amber-400">
                {busyId === s.id ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Preparing your sample…</>
                ) : (
                  <>Start this sample <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" /></>
                )}
              </div>
            </button>
          ))}
        </div>

        <div className="mt-12 max-w-xl space-y-2.5">
          {[
            "The full AI interview experience, live voice, avatar, real feedback",
            "Never uses a paid credit, samples are always free",
            "Up to 3 sample interviews per day",
          ].map((t) => (
            <div key={t} className="flex items-center gap-2.5 text-sm text-zinc-400">
              <Check className="h-4 w-4 text-amber-400 flex-shrink-0" strokeWidth={3} /> {t}
            </div>
          ))}
          <div className="pt-4 text-sm text-zinc-500">
            Ready to test your own application?{" "}
            <Link to="/register" data-testid="try-footer-signup" className="text-amber-400 hover:text-amber-300 font-medium">Create your free account</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
